"use client";

import React, { useEffect } from "react";
import Image from "next/image";
import { FieldValues, Path, useFormContext, Controller } from "react-hook-form";
import { Field as BaseField } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "@/components/ui/input-group"
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { favicon, hostname, isValidDomain, removeSchema } from "@/services/fetch";

type Options = {
    [key: string]: string;
};

type Props<T extends FieldValues> = {
    label?: string;
    description?: string;
} & Omit<React.ComponentProps<typeof BaseField>, "name"> & { name: Path<T> };

type FieldProps<T extends FieldValues> = Props<T> & {
    children: React.ReactNode;
}

type InputProps<T extends FieldValues> = Props<T> & {
    placeholder?: string;
};

type NumberInputProps<T extends FieldValues> = InputProps<T> & {
    min?: number;
    max?: number;
};

type DropdownInputProps<T extends FieldValues> = Props<T> & {
    isLoading?: boolean;
    options: Options;
};

export function Field<T extends FieldValues>({ name, label, description, children, ...props }: FieldProps<T>) {
    const { formState: { errors }, control } = useFormContext<T>();
    const error = errors[name];

    return (
        <FormField name={name} control={control} render={({ field }) => (
            <FormItem>
                <FormLabel>{label}</FormLabel>
                <FormControl>{children}</FormControl>
                {!error ? (<FormDescription>{description}</FormDescription>) : null}
                <FormMessage />
            </FormItem>
        )} />
    );
}

export const StringInput = <T extends FieldValues>({ name, label, description, placeholder, ...props }: InputProps<T>) => {
    const { register } = useFormContext<T>();

    return (
        <Field name={name} label={label} description={description} {...props}>
            <Input id={name} type="text" placeholder={placeholder || label || name} {...register(name)} />
        </Field>
    );
}

export const NumberInput = <T extends FieldValues>({ name, label, description, placeholder, min, max, ...props }: NumberInputProps<T>) => {
    const { register } = useFormContext<T>();

    return (
        <Field name={name} label={label} description={description} {...props}>
            <Input id={name} type="number" placeholder={placeholder || label || name} min={min} max={max} inputMode="numeric" {...register(name, { setValueAs: value => Number(value) })} />
        </Field>
    );
}

export const EmailInput = <T extends FieldValues>({ name, label, description, placeholder, ...props }: InputProps<T>) => {
    const { register } = useFormContext<T>();

    return (
        <Field name={name} label={label} description={description} {...props}>
            <Input id={name} type="email" placeholder={placeholder || label || name} {...register(name)} />
        </Field>
    );
}

export const TextareaInput = <T extends FieldValues>({ name, label, description, placeholder, ...props }: InputProps<T>) => {
    const { register } = useFormContext<T>();

    return (
        <Field name={name} label={label} description={description} {...props}>
            <Textarea id={name} placeholder={placeholder || label || name} {...register(name)} />
        </Field>
    );
}

export const SecretInput = <T extends FieldValues>({ name, label, description, placeholder, ...props }: InputProps<T>) => {
    const { register } = useFormContext<T>();
    const [reveal, setReveal] = React.useState(false)
    const onClick = () => setReveal(!reveal)

    return (
        <Field name={name} label={label} description={description} {...props}>
            <InputGroup>
                <InputGroupInput id={name} type={reveal ? "text" : "password"} placeholder={placeholder || label || name} {...register(name)} />
                <InputGroupAddon align="inline-end">
                    <InputGroupButton type="button" onClick={onClick}>
                        {reveal ? "Hide" : "Reveal"}
                    </InputGroupButton>
                </InputGroupAddon>
            </InputGroup>
        </Field>
    );
}

export const WebsiteInput = <T extends FieldValues>({ name, label, description, placeholder, ...props }: InputProps<T>) => {
    const { register, watch } = useFormContext<T>();
    const [host, setHost] = React.useState<string>("");
    const [faviconSrc, setFaviconSrc] = React.useState<string>("");
    const { onChange, ...rest } = register(name);

    const value = watch(name);

    useEffect(() => {
        setFaviconSrc(host ? favicon(host) : "");
    }, [host]);

    useEffect(() => {
        if (!value) {
            if (host) setHost("");
            return;
        }

        const candidate = hostname(value);
        if (host !== candidate && isValidDomain(candidate)) {
            setHost(candidate);
        }
    }, [value, host]);

    return (
        <Field name={name} label={label} description={description} {...props}>
            <InputGroup>
                <InputGroupAddon>
                    {faviconSrc ? <Image src={faviconSrc} alt="favicon" width={16} height={16} /> : <div style={{ width: 16, height: 16 }} />}
                    https://
                </InputGroupAddon>
                <InputGroupInput id={name} placeholder={placeholder || label || name} onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const value = removeSchema(event.target.value);
                    if (!value) {
                        setHost('');
                    } else if (isValidDomain(value)) {
                        const candidate = value.split('/')[0];
                        if (host !== candidate) {
                            setHost(candidate);
                        }
                    }

                    event.target.value = value
                    onChange(event)
                }} {...rest} />
            </InputGroup>
        </Field>
    );
}

const Dropdown = ({ field, options, name }: { field: any, options: Options, name: string }) => {
    return (
        <Select onValueChange={field.onChange} value={field.value} key={field.value}>
            <SelectTrigger>
                <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
                {Object.keys(options).map((key) => (
                    <SelectItem key={key} value={key}>{options[key]}</SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
};

export const DropdownInput = <T extends FieldValues>({ name, label, description, options, isLoading, ...props }: DropdownInputProps<T>) => {
    const { control } = useFormContext<T>();

    return (
        <Field name={name} label={label} description={description} {...props}>
            <Controller control={control} name={name} render={({ field }) => (
                <Dropdown field={field} options={options} name={name} />
            )}
            />
        </Field>
    );
}

export const HiddenInput = ({ name, value, ...props }: { name: string, value: string }) => {
    const { register } = useFormContext()
;
    return (
        <Input type="hidden" id={name} value={value} {...register(name)} {...props} />
    );
}